/**
 * CBAM (Carbon Border Adjustment Mechanism) Module
 * Product-Wise Carbon Engine for EU CBAM compliance
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Package, Plus, Edit2, Trash2, Save, X, Check, AlertTriangle,
  Factory, Zap, Flame, TrendingUp, DollarSign, Ship, Globe,
  BarChart3, FileText, Settings, RefreshCw, Lock, Unlock,
  ChevronRight, ChevronDown, Download, Calculator, Leaf,
  Database, ClipboardList, CheckCircle, Clock, ArrowRight, ExternalLink
} from 'lucide-react';
import { useAlert } from '../../context/AlertContext';

const API = process.env.REACT_APP_BACKEND_URL;

const CBAMModule = ({ study }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [moduleStatus, setModuleStatus] = useState(null);
  const [products, setProducts] = useState([]);
  const [productions, setProductions] = useState([]);
  const [energyData, setEnergyData] = useState(null);
  const [emissionFactors, setEmissionFactors] = useState([]);
  const [allocation, setAllocation] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [settings, setSettings] = useState({ carbon_price_eur: 80, allocation_method: 'energy' });
  
  // Phase 2 states
  const [currencyInfo, setCurrencyInfo] = useState(null);
  const [workflowStatus, setWorkflowStatus] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [batches, setBatches] = useState([]);
  const [renewableOffset, setRenewableOffset] = useState(null);
  
  // Audit Log states
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditSummary, setAuditSummary] = useState(null);
  const [auditFilters, setAuditFilters] = useState({ entity_type: '', user_email: '' });
  
  // Form states
  const [showProductForm, setShowProductForm] = useState(false);
  const [showShipmentForm, setShowShipmentForm] = useState(false);
  const [showBatchForm, setShowBatchForm] = useState(false);
  const [showEvidenceForm, setShowEvidenceForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('2025-04');
  const [allocationMethod, setAllocationMethod] = useState('energy');

  useEffect(() => {
    if (study?.id) {
      fetchAllData();
    }
  }, [study?.id]);

  const fetchAllData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      const [statusRes, productsRes, settingsRes, dashboardRes, currencyRes, workflowRes] = await Promise.all([
        axios.get(`${API}/api/cbam/${study.id}/status`, config),
        axios.get(`${API}/api/cbam/${study.id}/products`, config),
        axios.get(`${API}/api/cbam/${study.id}/settings`, config),
        axios.get(`${API}/api/cbam/${study.id}/dashboard`, config),
        axios.get(`${API}/api/cbam/${study.id}/currency-info`, config),
        axios.get(`${API}/api/cbam/${study.id}/workflow-status`, config)
      ]);

      setModuleStatus(statusRes.data);
      setProducts(productsRes.data);
      setSettings(settingsRes.data);
      setDashboard(dashboardRes.data);
      setCurrencyInfo(currencyRes.data);
      setWorkflowStatus(workflowRes.data);
    } catch (error) {
      console.error('Error fetching CBAM data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/cbam/${study.id}/products`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setProducts(res.data);
  };

  const fetchProductions = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/cbam/${study.id}/production?period=${selectedPeriod}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setProductions(res.data);
  };

  const fetchEnergyData = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/cbam/${study.id}/energy-data`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setEnergyData(res.data);
  };

  const fetchEmissionFactors = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/cbam/${study.id}/emission-factors`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setEmissionFactors(res.data);
  };

  const fetchAllocation = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(
        `${API}/api/cbam/${study.id}/allocation?period=${selectedPeriod}&method=${allocationMethod}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAllocation(res.data);
    } catch (error) {
      console.error('Error fetching allocation:', error);
    }
  };

  const fetchShipments = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/cbam/${study.id}/shipments`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setShipments(res.data);
  };

  const fetchDashboard = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/cbam/${study.id}/dashboard`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setDashboard(res.data);
  };

  // Phase 2 fetch functions
  const fetchBatches = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/cbam/${study.id}/batches`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setBatches(res.data);
  };

  const fetchEvidence = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/cbam/${study.id}/evidence`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setEvidence(res.data);
  };

  const fetchRenewableOffset = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/cbam/${study.id}/renewable-offset-summary`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setRenewableOffset(res.data);
  };

  const fetchAuditLogs = async () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    if (auditFilters.entity_type) params.append('entity_type', auditFilters.entity_type);
    if (auditFilters.user_email) params.append('user_email', auditFilters.user_email);
    
    const res = await axios.get(`${API}/api/cbam/${study.id}/audit-log?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setAuditLogs(res.data.logs || res.data);
  };

  const fetchAuditSummary = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/cbam/${study.id}/audit-log/summary`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setAuditSummary(res.data);
  };

  const fetchWorkflow = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/cbam/${study.id}/workflow-status`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setWorkflowStatus(res.data);
  };

  // Handle tab changes
  useEffect(() => {
    if (!study?.id) return;
    
    if (activeTab === 'products') fetchProducts();
    else if (activeTab === 'production') { fetchProducts(); fetchProductions(); }
    else if (activeTab === 'energy') { fetchEnergyData(); fetchEmissionFactors(); }
    else if (activeTab === 'allocation') { fetchProducts(); fetchProductions(); fetchAllocation(); }
    else if (activeTab === 'shipments') { fetchProducts(); fetchShipments(); }
    else if (activeTab === 'dashboard') fetchDashboard();
    else if (activeTab === 'batches') { fetchProducts(); fetchBatches(); }
    else if (activeTab === 'evidence') { fetchEvidence(); fetchRenewableOffset(); }
    else if (activeTab === 'workflow') fetchWorkflow();
    else if (activeTab === 'audit') { fetchAuditLogs(); fetchAuditSummary(); }
  }, [activeTab, study?.id, selectedPeriod, allocationMethod]);

  // Save product
  const saveProduct = async (productData) => {
    const token = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      if (editingProduct) {
        await axios.put(`${API}/api/cbam/${study.id}/products/${editingProduct.id}`, productData, config);
      } else {
        await axios.post(`${API}/api/cbam/${study.id}/products`, productData, config);
      }
      setShowProductForm(false);
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      showError('Error saving product: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Delete product
  const deleteProduct = async (productId) => {
    const confirmed = await confirm('Are you sure you want to delete this product?');
    if (!confirmed) return;

    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/api/cbam/${study.id}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchProducts();
    } catch (error) {
      showError('Error deleting product');
    }
  };

  // Save production
  const saveProduction = async (productId, qty) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/cbam/${study.id}/production`, {
        period: selectedPeriod,
        product_id: productId,
        production_qty: parseFloat(qty) || 0
      }, { headers: { Authorization: `Bearer ${token}` } });
      fetchProductions();
    } catch (error) {
      showError('Error saving production data');
    }
  };

  // Lock allocation
  const lockAllocation = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${API}/api/cbam/${study.id}/allocation/lock?period=${selectedPeriod}&method=${allocationMethod}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Allocation locked successfully!');
      fetchAllocation();
    } catch (error) {
      showError('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Create shipment
  const createShipment = async (shipmentData) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${API}/api/cbam/${study.id}/shipments?carbon_price_eur=${settings.carbon_price_eur}`,
        shipmentData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowShipmentForm(false);
      fetchShipments();
      fetchDashboard();
    } catch (error) {
      showError('Error creating shipment: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Save settings
  const saveSettings = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/cbam/${study.id}/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Settings saved!');
    } catch (error) {
      showError('Error saving settings');
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'products', label: 'Product Master', icon: Package },
    { id: 'production', label: 'Production Data', icon: Factory },
    { id: 'energy', label: 'Energy & Emissions', icon: Zap },
    { id: 'allocation', label: 'Allocation Engine', icon: Calculator },
    { id: 'shipments', label: 'Shipments', icon: Ship },
    { id: 'batches', label: 'Batch Capture', icon: ClipboardList },
    { id: 'evidence', label: 'Evidence Locker', icon: FileText },
    { id: 'workflow', label: 'Approval', icon: CheckCircle },
    { id: 'dashboard', label: 'Cost Exposure', icon: DollarSign },
    { id: 'audit', label: 'Audit Log', icon: Database }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
        <span className="ml-3 text-gray-600">Loading CBAM module...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-1 flex items-center gap-2">
              <Leaf className="w-6 h-6" /> CBAM Engine
            </h2>
            <p className="text-emerald-200">Product-Wise Carbon Tracking for EU CBAM Compliance</p>
            <p className="text-sm text-emerald-300 mt-1">{study?.study_name}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-lg px-4 py-2 text-center">
              <p className="text-2xl font-bold">€{settings.carbon_price_eur}</p>
              <p className="text-xs text-emerald-200">Carbon Price/tCO₂</p>
            </div>
            {moduleStatus?.ready_for_declaration && (
              <div className="bg-green-400 text-green-900 rounded-lg px-4 py-2 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Ready for Declaration</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-emerald-600 text-emerald-600 bg-emerald-50'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <OverviewTab status={moduleStatus} settings={settings} onSettingsChange={setSettings} onSaveSettings={saveSettings} />
          )}

          {/* Products Tab */}
          {activeTab === 'products' && (
            <ProductsTab
              products={products}
              onAdd={() => { setEditingProduct(null); setShowProductForm(true); }}
              onEdit={(p) => { setEditingProduct(p); setShowProductForm(true); }}
              onDelete={deleteProduct}
            />
          )}

          {/* Production Tab */}
          {activeTab === 'production' && (
            <ProductionTab
              products={products}
              productions={productions}
              selectedPeriod={selectedPeriod}
              onPeriodChange={setSelectedPeriod}
              onSave={saveProduction}
            />
          )}

          {/* Energy Tab */}
          {activeTab === 'energy' && (
            <EnergyTab energyData={energyData} emissionFactors={emissionFactors} />
          )}

          {/* Allocation Tab */}
          {activeTab === 'allocation' && (
            <AllocationTab
              allocation={allocation}
              method={allocationMethod}
              onMethodChange={(m) => { setAllocationMethod(m); }}
              selectedPeriod={selectedPeriod}
              onPeriodChange={setSelectedPeriod}
              onLock={lockAllocation}
              onRefresh={fetchAllocation}
            />
          )}

          {/* Shipments Tab */}
          {activeTab === 'shipments' && (
            <ShipmentsTab
              shipments={shipments}
              products={products}
              carbonPrice={settings.carbon_price_eur}
              onAdd={() => setShowShipmentForm(true)}
            />
          )}

          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <DashboardTab dashboard={dashboard} />
          )}

          {/* Phase 2: Batch Capture Tab */}
          {activeTab === 'batches' && (
            <BatchCaptureTab
              batches={batches}
              products={products}
              studyId={study.id}
              onRefresh={fetchBatches}
            />
          )}

          {/* Phase 2: Evidence Locker Tab */}
          {activeTab === 'evidence' && (
            <EvidenceTab
              evidence={evidence}
              renewableOffset={renewableOffset}
              studyId={study.id}
              onRefresh={() => { fetchEvidence(); fetchRenewableOffset(); }}
            />
          )}

          {/* Phase 2: Approval Workflow Tab */}
          {activeTab === 'workflow' && (
            <WorkflowTab
              workflow={workflowStatus}
              currencyInfo={currencyInfo}
              studyId={study.id}
              onRefresh={fetchWorkflow}
            />
          )}

          {/* Audit Log Tab */}
          {activeTab === 'audit' && (
            <AuditLogTab
              logs={auditLogs}
              summary={auditSummary}
              filters={auditFilters}
              onFilterChange={setAuditFilters}
              onRefresh={() => { fetchAuditLogs(); fetchAuditSummary(); }}
            />
          )}
        </div>
      </div>

      {/* Product Form Modal */}
      {showProductForm && (
        <ProductFormModal
          product={editingProduct}
          onSave={saveProduct}
          onClose={() => { setShowProductForm(false); setEditingProduct(null); }}
        />
      )}

      {/* Shipment Form Modal */}
      {showShipmentForm && (
        <ShipmentFormModal
          products={products}
          carbonPrice={settings.carbon_price_eur}
          onSave={createShipment}
          onClose={() => setShowShipmentForm(false)}
        />
      )}
    </div>
  );
};

// ==================== SUB-COMPONENTS ====================

const OverviewTab = ({ status, settings, onSettingsChange, onSaveSettings }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <StatusCard icon={Package} label="Products Defined" value={status?.products_defined || 0} color="blue" />
      <StatusCard icon={Factory} label="Production Records" value={status?.production_records || 0} color="orange" />
      <StatusCard icon={Zap} label="Energy Records" value={status?.energy_records || 0} color="yellow" />
      <StatusCard icon={Lock} label="Allocations Locked" value={status?.allocations_locked || 0} color="green" />
      <StatusCard icon={Ship} label="Shipments Created" value={status?.shipments_created || 0} color="purple" />
      <StatusCard icon={ClipboardList} label="Batches Captured" value={status?.batches_captured || 0} color="pink" />
    </div>

    <div className="bg-gray-50 rounded-lg p-6">
      <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
        <Settings className="w-5 h-5" /> CBAM Settings
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Carbon Price (€/tCO₂)</label>
          <input
            type="number"
            value={settings.carbon_price_eur}
            onChange={(e) => onSettingsChange({ ...settings, carbon_price_eur: parseFloat(e.target.value) || 0 })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Default Allocation Method</label>
          <select
            value={settings.allocation_method}
            onChange={(e) => onSettingsChange({ ...settings, allocation_method: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="energy">Energy-Based</option>
            <option value="mass">Mass-Based</option>
            <option value="process">Process-Specific</option>
          </select>
        </div>
        <div className="flex items-end">
          <button
            onClick={onSaveSettings}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 flex items-center gap-2"
          >
            <Save className="w-4 h-4" /> Save Settings
          </button>
        </div>
      </div>
    </div>

    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
      <h3 className="font-bold text-emerald-900 mb-3">CBAM Workflow</h3>
      <div className="flex items-center gap-2 text-sm text-emerald-800 flex-wrap">
        <span className="bg-emerald-100 px-3 py-1 rounded">1. Define Products</span>
        <ArrowRight className="w-4 h-4" />
        <span className="bg-emerald-100 px-3 py-1 rounded">2. Enter Production</span>
        <ArrowRight className="w-4 h-4" />
        <span className="bg-emerald-100 px-3 py-1 rounded">3. Sync Energy</span>
        <ArrowRight className="w-4 h-4" />
        <span className="bg-emerald-100 px-3 py-1 rounded">4. Allocate CO₂</span>
        <ArrowRight className="w-4 h-4" />
        <span className="bg-emerald-100 px-3 py-1 rounded">5. Log Shipments</span>
        <ArrowRight className="w-4 h-4" />
        <span className="bg-emerald-100 px-3 py-1 rounded">6. Export CBAM</span>
      </div>
    </div>
  </div>
);

const StatusCard = ({ icon: Icon, label, value, color }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    orange: 'bg-orange-50 text-orange-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    pink: 'bg-pink-50 text-pink-600'
  };

  return (
    <div className={`${colors[color]} rounded-lg p-4`}>
      <div className="flex items-center gap-3">
        <Icon className="w-8 h-8" />
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-xs opacity-80">{label}</p>
        </div>
      </div>
    </div>
  );
};

const ProductsTab = ({ products, onAdd, onEdit, onDelete }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-bold text-gray-900">Product Master</h3>
      <button
        onClick={onAdd}
        className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 flex items-center gap-2"
      >
        <Plus className="w-4 h-4" /> Add Product
      </button>
    </div>

    {/* CBAM v2: Sector-Specific Guidance */}
    <div className="grid grid-cols-2 gap-3 mb-4">
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
        <p className="text-xs font-semibold text-orange-900">🏭 Cement Products</p>
        <p className="text-xs text-orange-800">Calcination CO₂ required before export</p>
      </div>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-xs font-semibold text-blue-900">⚡ Aluminium Products</p>
        <p className="text-xs text-blue-800">Track electricity dominance (&gt;85% flag)</p>
      </div>
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
        <p className="text-xs font-semibold text-purple-900">🔥 Iron & Steel</p>
        <p className="text-xs text-purple-800">Process-specific preferred for furnaces</p>
      </div>
      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
        <p className="text-xs font-semibold text-green-900">🧪 Fertilisers</p>
        <p className="text-xs text-green-800">Chemical CO₂ + H₂ pathway required</p>
      </div>
    </div>

    {products.length === 0 ? (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600">No products defined yet.</p>
        <p className="text-sm text-gray-500">Add products with HS codes for CBAM tracking.</p>
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Product Name</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">HS Code</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Process Route</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">CBAM Applicable</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {products.map(product => (
              <tr key={product.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-900">{product.product_name}</td>
                <td className="px-4 py-3 font-mono text-sm text-gray-600">{product.hs_code}</td>
                <td className="px-4 py-3 text-gray-600">{product.process_route || '-'}</td>
                <td className="px-4 py-3 text-center">
                  {product.cbam_applicable ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      <Check className="w-3 h-3 mr-1" /> Yes
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                      No
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  <button onClick={() => onEdit(product)} className="p-1 text-blue-600 hover:bg-blue-50 rounded">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => onDelete(product.id)} className="p-1 text-red-600 hover:bg-red-50 rounded ml-2">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const ProductionTab = ({ products, productions, selectedPeriod, onPeriodChange, onSave }) => {
  const [editValues, setEditValues] = useState({});

  const getProductionQty = (productId) => {
    const prod = productions.find(p => p.product_id === productId);
    return prod?.production_qty || 0;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">Production Data</h3>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Period:</label>
          <input
            type="month"
            value={selectedPeriod}
            onChange={(e) => onPeriodChange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>
      </div>

      {products.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Factory className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No products defined yet.</p>
          <p className="text-sm text-gray-500">Add products first in the Product Master tab.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Product</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">HS Code</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Production (MT)</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {products.filter(p => p.cbam_applicable).map(product => (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{product.product_name}</td>
                  <td className="px-4 py-3 font-mono text-sm text-gray-600">{product.hs_code}</td>
                  <td className="px-4 py-3 text-right">
                    <input
                      type="number"
                      defaultValue={getProductionQty(product.id)}
                      onChange={(e) => setEditValues({ ...editValues, [product.id]: e.target.value })}
                      className="w-32 px-3 py-1 border border-gray-300 rounded text-right"
                      placeholder="0"
                    />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => onSave(product.id, editValues[product.id] || getProductionQty(product.id))}
                      className="px-3 py-1 bg-emerald-600 text-white rounded text-sm hover:bg-emerald-700"
                    >
                      Save
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const EnergyTab = ({ energyData, emissionFactors }) => {
  if (!energyData) return <div className="text-center py-8">Loading energy data...</div>;

  const emissions = energyData.emissions || {};

  return (
    <div className="space-y-6">
      {/* Emissions Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-orange-50 rounded-lg p-4">
          <div className="flex items-center gap-2 text-orange-600 mb-2">
            <Flame className="w-5 h-5" />
            <span className="font-semibold">Scope 1</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{emissions.scope1_tco2?.toFixed(2) || 0}</p>
          <p className="text-xs text-gray-500">tCO₂ (Direct)</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center gap-2 text-blue-600 mb-2">
            <Zap className="w-5 h-5" />
            <span className="font-semibold">Scope 2</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{emissions.scope2_tco2?.toFixed(2) || 0}</p>
          <p className="text-xs text-gray-500">tCO₂ (Electricity)</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center gap-2 text-green-600 mb-2">
            <Leaf className="w-5 h-5" />
            <span className="font-semibold">Renewable Offset</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{emissions.renewable_offset_tco2?.toFixed(2) || 0}</p>
          <p className="text-xs text-gray-500">tCO₂</p>
        </div>
        <div className="bg-gray-100 rounded-lg p-4">
          <div className="flex items-center gap-2 text-gray-700 mb-2">
            <Globe className="w-5 h-5" />
            <span className="font-semibold">Total CBAM</span>
          </div>
          <p className="text-2xl font-bold text-emerald-600">{emissions.total_tco2?.toFixed(2) || 0}</p>
          <p className="text-xs text-gray-500">tCO₂e</p>
        </div>
      </div>

      {/* Electricity Data */}
      <div>
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Zap className="w-4 h-4 text-yellow-500" /> Electricity Data (from ERS)
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Period</th>
                <th className="px-3 py-2 text-right">kWh</th>
                <th className="px-3 py-2 text-right">Bill (₹)</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(energyData.electricity || []).slice(0, 12).map((e, i) => (
                <tr key={i}>
                  <td className="px-3 py-2">{e.month}</td>
                  <td className="px-3 py-2 text-right">{(e.consumed_units_kwh || 0).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">{(e.bill_amount || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Fuel Data */}
      <div>
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Flame className="w-4 h-4 text-orange-500" /> Fuel Data (from ERS)
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Period</th>
                <th className="px-3 py-2 text-left">Fuel Type</th>
                <th className="px-3 py-2 text-right">Quantity</th>
                <th className="px-3 py-2 text-left">Unit</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(energyData.fuels || []).slice(0, 12).map((f, i) => (
                <tr key={i}>
                  <td className="px-3 py-2">{f.month}</td>
                  <td className="px-3 py-2">{f.fuel_type}</td>
                  <td className="px-3 py-2 text-right">{(f.quantity || 0).toLocaleString()}</td>
                  <td className="px-3 py-2">{f.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Emission Factors */}
      <div>
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Database className="w-4 h-4 text-indigo-500" /> Emission Factors
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Fuel Type</th>
                <th className="px-3 py-2 text-left">Unit</th>
                <th className="px-3 py-2 text-right">EF (tCO₂/unit)</th>
                <th className="px-3 py-2 text-left">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {emissionFactors.map((ef, i) => (
                <tr key={i}>
                  <td className="px-3 py-2 font-medium">{ef.fuel_type}</td>
                  <td className="px-3 py-2">{ef.unit}</td>
                  <td className="px-3 py-2 text-right font-mono">{ef.emission_factor}</td>
                  <td className="px-3 py-2 text-gray-500">{ef.source || ef.version}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const AllocationTab = ({ allocation, method, onMethodChange, selectedPeriod, onPeriodChange, onLock, onRefresh }) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <h3 className="text-lg font-bold text-gray-900">Carbon Allocation Engine</h3>
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Period:</label>
          <input
            type="month"
            value={selectedPeriod}
            onChange={(e) => onPeriodChange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Method:</label>
          <select
            value={method}
            onChange={(e) => onMethodChange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="energy">Energy-Based</option>
            <option value="mass">Mass-Based</option>
            <option value="process">Process-Specific</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Granularity:</label>
          <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="batch">Batch-level</option>
            <option value="equipment">Equipment-level</option>
            <option value="product">Product-level</option>
            <option value="plant">Plant-level</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Metering:</label>
          <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="direct">Direct</option>
            <option value="proxy">Proxy/Benchmark</option>
            <option value="none">None</option>
          </select>
        </div>
        <button onClick={onRefresh} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
    </div>

    {allocation && (
      <>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-100 rounded-lg p-4">
            <p className="text-sm text-gray-600">Total Plant CO₂</p>
            <p className="text-2xl font-bold text-gray-900">{allocation.total_plant_co2_tco2?.toFixed(3)} t</p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-4">
            <p className="text-sm text-gray-600">Allocated Total</p>
            <p className="text-2xl font-bold text-emerald-600">{allocation.allocated_total_tco2?.toFixed(3)} t</p>
          </div>
          <div className={`rounded-lg p-4 ${allocation.allocation_valid ? 'bg-green-50' : 'bg-red-50'}`}>
            <p className="text-sm text-gray-600">Tolerance</p>
            <p className={`text-2xl font-bold ${allocation.allocation_valid ? 'text-green-600' : 'text-red-600'}`}>
              {allocation.tolerance_percent}%
            </p>
          </div>
          <div className="flex items-center justify-center">
            <button
              onClick={onLock}
              disabled={!allocation.allocation_valid}
              className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
                allocation.allocation_valid
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <Lock className="w-4 h-4" /> Lock Allocation
            </button>
          </div>
        </div>

        {/* Validation */}
        {!allocation.allocation_valid && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <div>
              <p className="font-medium text-red-800">Allocation Mismatch</p>
              <p className="text-sm text-red-600">
                Difference of {allocation.tolerance_percent}% exceeds 1% tolerance. Cannot lock allocation.
              </p>
            </div>
          </div>
        )}

        {/* CBAM v2: Confidence Level Info */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <p className="font-semibold text-blue-900 mb-2">CBAM v2: Confidence Levels</p>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">🟢 High</span>
                  <span className="text-blue-800">Process-specific allocation</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">🟡 Medium</span>
                  <span className="text-blue-800">Energy-based allocation</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">🔴 Low</span>
                  <span className="text-blue-800">Mass-based allocation</span>
                </div>
              </div>
              <p className="text-xs text-blue-700 mt-2">
                Confidence reflects data and allocation strength, not regulatory validity. Higher confidence increases EU importer trust.
              </p>
            </div>
          </div>
        </div>

        {/* Allocations Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Product</th>
                {method === 'energy' && <th className="px-4 py-3 text-right text-sm font-semibold">Energy (kWh)</th>}
                <th className="px-4 py-3 text-right text-sm font-semibold">{method === 'energy' ? 'Energy Share' : 'Mass Share'}</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Production (MT)</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Allocated CO₂ (t)</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Intensity (tCO₂/MT)</th>
                <th className="px-4 py-3 text-center text-sm font-semibold">Confidence</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(allocation.allocations || []).map((alloc, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{alloc.product_name}</td>
                  {method === 'energy' && <td className="px-4 py-3 text-right">{(alloc.energy_kwh || 0).toLocaleString()}</td>}
                  <td className="px-4 py-3 text-right">{alloc.energy_share_percent || alloc.mass_share_percent}%</td>
                  <td className="px-4 py-3 text-right">{alloc.production_mt?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-medium text-emerald-600">{alloc.allocated_co2_tco2?.toFixed(4)}</td>
                  <td className="px-4 py-3 text-right font-mono">{alloc.intensity_tco2_per_mt?.toFixed(6)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      method === 'process' ? 'bg-green-100 text-green-700' :
                      method === 'energy' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`} title="Confidence reflects data and allocation strength, not regulatory validity">
                      {method === 'process' ? '🟢 High' : method === 'energy' ? '🟡 Medium' : '🔴 Low'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    )}
  </div>
);

const ShipmentsTab = ({ shipments, products, carbonPrice, onAdd }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-bold text-gray-900">EU Shipments</h3>
      <button
        onClick={onAdd}
        className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 flex items-center gap-2"
      >
        <Plus className="w-4 h-4" /> Add Shipment
      </button>
    </div>

    {shipments.length === 0 ? (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <Ship className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600">No shipments recorded yet.</p>
        <p className="text-sm text-gray-500">Add EU export shipments to calculate CBAM liability.</p>
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold">Product</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">HS Code</th>
              <th className="px-4 py-3 text-right text-sm font-semibold">Qty (MT)</th>
              <th className="px-4 py-3 text-right text-sm font-semibold">Intensity</th>
              <th className="px-4 py-3 text-right text-sm font-semibold">CO₂ (t)</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Destination</th>
              <th className="px-4 py-3 text-right text-sm font-semibold">CBAM Cost (€)</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {shipments.map((ship, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium">{ship.product_name}</td>
                <td className="px-4 py-3 font-mono text-sm">{ship.hs_code}</td>
                <td className="px-4 py-3 text-right">{ship.quantity_mt?.toLocaleString()}</td>
                <td className="px-4 py-3 text-right font-mono text-sm">{ship.intensity_tco2_per_mt?.toFixed(4)}</td>
                <td className="px-4 py-3 text-right font-medium text-emerald-600">{ship.shipment_co2_tco2?.toFixed(2)}</td>
                <td className="px-4 py-3">{ship.destination_country}</td>
                <td className="px-4 py-3 text-right font-bold text-orange-600">€{ship.cbam_cost_eur?.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-100">
            <tr>
              <td colSpan={2} className="px-4 py-3 font-bold">Total</td>
              <td className="px-4 py-3 text-right font-bold">{shipments.reduce((s, x) => s + (x.quantity_mt || 0), 0).toLocaleString()}</td>
              <td className="px-4 py-3"></td>
              <td className="px-4 py-3 text-right font-bold text-emerald-600">{shipments.reduce((s, x) => s + (x.shipment_co2_tco2 || 0), 0).toFixed(2)}</td>
              <td className="px-4 py-3"></td>
              <td className="px-4 py-3 text-right font-bold text-orange-600">€{shipments.reduce((s, x) => s + (x.cbam_cost_eur || 0), 0).toLocaleString()}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    )}
  </div>
);

const DashboardTab = ({ dashboard }) => {
  if (!dashboard) return <div className="text-center py-8">Loading dashboard...</div>;

  const summary = dashboard.summary || {};

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-sm text-blue-600">Total Shipments</p>
          <p className="text-3xl font-bold text-gray-900">{summary.total_shipments || 0}</p>
        </div>
        <div className="bg-orange-50 rounded-lg p-4">
          <p className="text-sm text-orange-600">Total Quantity</p>
          <p className="text-3xl font-bold text-gray-900">{summary.total_quantity_mt?.toLocaleString() || 0} MT</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-4">
          <p className="text-sm text-emerald-600">Embedded CO₂</p>
          <p className="text-3xl font-bold text-gray-900">{summary.total_embedded_co2_tco2?.toFixed(2) || 0} t</p>
        </div>
        <div className="bg-red-50 rounded-lg p-4">
          <p className="text-sm text-red-600">CBAM Liability</p>
          <p className="text-3xl font-bold text-red-600">€{summary.total_cbam_cost_eur?.toLocaleString() || 0}</p>
        </div>
      </div>

      {/* Carbon Price & Intensity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Carbon Price Used</p>
          <p className="text-2xl font-bold text-gray-900">€{summary.carbon_price_eur}/tCO₂</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Average Intensity</p>
          <p className="text-2xl font-bold text-gray-900">{summary.avg_intensity_tco2_per_mt?.toFixed(4) || 0} tCO₂/MT</p>
        </div>
      </div>

      {/* By Destination */}
      {Object.keys(dashboard.by_destination || {}).length > 0 && (
        <div>
          <h4 className="font-semibold text-gray-900 mb-3">By Destination</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(dashboard.by_destination).map(([dest, data]) => (
              <div key={dest} className="bg-white border rounded-lg p-4">
                <p className="font-medium text-gray-900 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-blue-500" /> {dest}
                </p>
                <div className="mt-2 text-sm text-gray-600 space-y-1">
                  <p>Quantity: {data.quantity_mt?.toLocaleString()} MT</p>
                  <p>CO₂: {data.co2_tco2?.toFixed(2)} t</p>
                  <p className="font-medium text-orange-600">Cost: €{data.cost_eur?.toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* By Product */}
      {Object.keys(dashboard.by_product || {}).length > 0 && (
        <div>
          <h4 className="font-semibold text-gray-900 mb-3">By Product</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Product</th>
                  <th className="px-4 py-2 text-right">Quantity (MT)</th>
                  <th className="px-4 py-2 text-right">CO₂ (t)</th>
                  <th className="px-4 py-2 text-right">CBAM Cost (€)</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {Object.entries(dashboard.by_product).map(([prod, data]) => (
                  <tr key={prod}>
                    <td className="px-4 py-2 font-medium">{prod}</td>
                    <td className="px-4 py-2 text-right">{data.quantity_mt?.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right">{data.co2_tco2?.toFixed(2)}</td>
                    <td className="px-4 py-2 text-right font-medium text-orange-600">€{data.cost_eur?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== MODALS ====================

const ProductFormModal = ({ product, onSave, onClose }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [form, setForm] = useState({
    product_name: product?.product_name || '',
    hs_code: product?.hs_code || '',
    process_route: product?.process_route || '',
    unit: 'MT',
    cbam_applicable: product?.cbam_applicable ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.hs_code) {
      showWarning('HS Code is mandatory for CBAM products');
      return;
    }
    onSave(form);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
        <div className="bg-emerald-600 px-6 py-4 rounded-t-xl">
          <h3 className="text-lg font-bold text-white">{product ? 'Edit Product' : 'Add Product'}</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
            <input
              type="text"
              value={form.product_name}
              onChange={(e) => setForm({ ...form, product_name: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="e.g., Hot Rolled Coil (HRC)"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">HS Code * (Mandatory for CBAM)</label>
            <input
              type="text"
              value={form.hs_code}
              onChange={(e) => setForm({ ...form, hs_code: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono"
              placeholder="e.g., 7208.10"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Process Route</label>
            <select
              value={form.process_route}
              onChange={(e) => setForm({ ...form, process_route: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Select process route</option>
              <option value="BF-BOF">BF-BOF (Blast Furnace)</option>
              <option value="EAF">EAF (Electric Arc Furnace)</option>
              <option value="DRI-EAF">DRI-EAF</option>
              <option value="Casting">Casting</option>
              <option value="Rolling">Rolling</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
            <input
              type="text"
              value="MT (Metric Tonnes)"
              disabled
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
            />
            <p className="text-xs text-gray-500 mt-1">Unit is locked to MT for CBAM compliance</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="cbam_applicable"
              checked={form.cbam_applicable}
              onChange={(e) => setForm({ ...form, cbam_applicable: e.target.checked })}
              className="w-4 h-4 text-emerald-600 rounded"
            />
            <label htmlFor="cbam_applicable" className="text-sm text-gray-700">CBAM Applicable</label>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2">
              <Save className="w-4 h-4" /> {product ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ShipmentFormModal = ({ products, carbonPrice, onSave, onClose }) => {
  const [form, setForm] = useState({
    product_id: '',
    quantity_mt: '',
    destination_country: '',
    shipment_date: new Date().toISOString().split('T')[0],
    invoice_number: ''
  });

  const euCountries = [
    'Austria', 'Belgium', 'Bulgaria', 'Croatia', 'Cyprus', 'Czech Republic',
    'Denmark', 'Estonia', 'Finland', 'France', 'Germany', 'Greece', 'Hungary',
    'Ireland', 'Italy', 'Latvia', 'Lithuania', 'Luxembourg', 'Malta', 'Netherlands',
    'Poland', 'Portugal', 'Romania', 'Slovakia', 'Slovenia', 'Spain', 'Sweden'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      quantity_mt: parseFloat(form.quantity_mt) || 0
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
        <div className="bg-emerald-600 px-6 py-4 rounded-t-xl">
          <h3 className="text-lg font-bold text-white">Add EU Shipment</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product *</label>
            <select
              value={form.product_id}
              onChange={(e) => setForm({ ...form, product_id: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Select product</option>
              {products.filter(p => p.cbam_applicable).map(p => (
                <option key={p.id} value={p.id}>{p.product_name} ({p.hs_code})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quantity (MT) *</label>
            <input
              type="number"
              value={form.quantity_mt}
              onChange={(e) => setForm({ ...form, quantity_mt: e.target.value })}
              required
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="e.g., 500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Destination Country *</label>
            <select
              value={form.destination_country}
              onChange={(e) => setForm({ ...form, destination_country: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Select EU country</option>
              {euCountries.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Shipment Date</label>
              <input
                type="date"
                value={form.shipment_date}
                onChange={(e) => setForm({ ...form, shipment_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
              <input
                type="text"
                value={form.invoice_number}
                onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="INV-001"
              />
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
            <p>Carbon Price: <strong>€{carbonPrice}/tCO₂</strong></p>
            <p className="text-xs mt-1">CBAM cost will be calculated based on product intensity and this price.</p>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2">
              <Ship className="w-4 h-4" /> Create Shipment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== PHASE 2 COMPONENTS ====================

const BatchCaptureTab = ({ batches, products, studyId, onRefresh }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    batch_id: '',
    product_id: '',
    planned_qty_mt: '',
    shift: 'Day',
    actual_production_mt: '',
    scrap_reject_mt: '',
    electricity_start_kwh: '',
    electricity_end_kwh: '',
    fuel_type: '',
    fuel_quantity: ''
  });

  const saveBatch = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/cbam/${studyId}/batches`, {
        ...form,
        planned_qty_mt: parseFloat(form.planned_qty_mt) || 0,
        actual_production_mt: parseFloat(form.actual_production_mt) || 0,
        scrap_reject_mt: parseFloat(form.scrap_reject_mt) || 0,
        electricity_start_kwh: parseFloat(form.electricity_start_kwh) || null,
        electricity_end_kwh: parseFloat(form.electricity_end_kwh) || null,
        fuel_quantity: parseFloat(form.fuel_quantity) || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      setShowForm(false);
      setForm({ batch_id: '', product_id: '', planned_qty_mt: '', shift: 'Day', actual_production_mt: '', scrap_reject_mt: '', electricity_start_kwh: '', electricity_end_kwh: '', fuel_type: '', fuel_quantity: '' });
      onRefresh();
    } catch (error) {
      showError('Error saving batch: ' + (error.response?.data?.detail || error.message));
    }
  };

  const syncBatch = async (batchId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/cbam/${studyId}/batches/${batchId}/sync`, {}, { headers: { Authorization: `Bearer ${token}` } });
      onRefresh();
    } catch (error) {
      showError('Error syncing batch');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Mobile Batch Capture</h3>
          <p className="text-sm text-gray-500">Capture production batches with energy data from shop floor</p>
        </div>
        <button onClick={() => setShowForm(true)} className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 flex items-center gap-2">
          <Plus className="w-4 h-4" /> New Batch
        </button>
      </div>

      {batches.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <ClipboardList className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No batches captured yet.</p>
          <p className="text-sm text-gray-500">Use this feature to capture batch-level production and energy data.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Batch ID</th>
                <th className="px-3 py-2 text-left">Product</th>
                <th className="px-3 py-2 text-right">Planned (MT)</th>
                <th className="px-3 py-2 text-right">Actual (MT)</th>
                <th className="px-3 py-2 text-right">Net Good (MT)</th>
                <th className="px-3 py-2 text-right">Electricity (kWh)</th>
                <th className="px-3 py-2 text-center">Status</th>
                <th className="px-3 py-2 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {batches.map((batch, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono font-medium">{batch.batch_id}</td>
                  <td className="px-3 py-2">{batch.product_name || 'N/A'}</td>
                  <td className="px-3 py-2 text-right">{batch.planned_qty_mt}</td>
                  <td className="px-3 py-2 text-right">{batch.actual_production_mt || '-'}</td>
                  <td className="px-3 py-2 text-right font-medium">{batch.net_good_output_mt || '-'}</td>
                  <td className="px-3 py-2 text-right">{batch.batch_electricity_kwh?.toLocaleString() || '-'}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${batch.status === 'synced' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {batch.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {batch.status !== 'synced' && (
                      <button onClick={() => syncBatch(batch.id)} className="text-emerald-600 hover:text-emerald-800 text-sm font-medium">
                        Sync
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Batch Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-emerald-600 px-6 py-4 rounded-t-xl">
              <h3 className="text-lg font-bold text-white">New Batch Capture</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Batch ID</label>
                  <input type="text" value={form.batch_id} onChange={(e) => setForm({ ...form, batch_id: e.target.value })} placeholder="Auto-generated if empty" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Shift</label>
                  <select value={form.shift} onChange={(e) => setForm({ ...form, shift: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    <option value="Day">Day Shift</option>
                    <option value="Night">Night Shift</option>
                    <option value="General">General Shift</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product *</label>
                <select value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} required className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="">Select product</option>
                  {products.filter(p => p.cbam_applicable).map(p => (
                    <option key={p.id} value={p.id}>{p.product_name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Planned (MT)</label>
                  <input type="number" value={form.planned_qty_mt} onChange={(e) => setForm({ ...form, planned_qty_mt: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Actual (MT)</label>
                  <input type="number" value={form.actual_production_mt} onChange={(e) => setForm({ ...form, actual_production_mt: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Scrap (MT)</label>
                  <input type="number" value={form.scrap_reject_mt} onChange={(e) => setForm({ ...form, scrap_reject_mt: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-900 mb-3">Energy Capture</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Meter Start (kWh)</label>
                    <input type="number" value={form.electricity_start_kwh} onChange={(e) => setForm({ ...form, electricity_start_kwh: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Meter End (kWh)</label>
                    <input type="number" value={form.electricity_end_kwh} onChange={(e) => setForm({ ...form, electricity_end_kwh: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={saveBatch} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2">
                  <Save className="w-4 h-4" /> Save Batch
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EvidenceTab = ({ evidence, renewableOffset, studyId, onRefresh }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    evidence_type: 'renewable_certificate',
    title: '',
    description: '',
    energy_kwh: '',
    certificate_id: '',
    issuer: '',
    valid_from: '',
    valid_to: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const saveEvidence = async () => {
    const token = localStorage.getItem('token');
    setUploading(true);
    try {
      let document_url = '';
      let document_name = '';

      // Upload file if selected
      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);

        const uploadRes = await axios.post(`${API}/api/cbam/${studyId}/evidence/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        document_url = uploadRes.data.file_url;
        document_name = uploadRes.data.file_name;
      }

      // Save evidence metadata
      await axios.post(`${API}/api/cbam/${studyId}/evidence`, {
        ...form,
        energy_kwh: parseFloat(form.energy_kwh) || 0,
        document_url,
        document_name
      }, { headers: { Authorization: `Bearer ${token}` } });

      setShowForm(false);
      setForm({ evidence_type: 'renewable_certificate', title: '', description: '', energy_kwh: '', certificate_id: '', issuer: '', valid_from: '', valid_to: '' });
      setSelectedFile(null);
      onRefresh();
    } catch (error) {
      showError('Error saving evidence: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const verifyEvidence = async (evidenceId, verified) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/api/cbam/${studyId}/evidence/${evidenceId}/verify?verified=${verified}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      onRefresh();
    } catch (error) {
      showError('Error verifying evidence');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Evidence Locker</h3>
          <p className="text-sm text-gray-500">Renewable certificates and audit documents for CBAM compliance</p>
        </div>
        <button onClick={() => setShowForm(true)} className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Evidence
        </button>
      </div>

      {/* Renewable Offset Summary */}
      {renewableOffset && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
            <Leaf className="w-5 h-5" /> Renewable Energy Offset Summary
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-green-600">Total Renewable</p>
              <p className="text-xl font-bold text-green-900">{renewableOffset.total_renewable_kwh?.toLocaleString()} kWh</p>
            </div>
            <div>
              <p className="text-sm text-green-600">Verified Renewable</p>
              <p className="text-xl font-bold text-green-900">{renewableOffset.verified_renewable_kwh?.toLocaleString()} kWh</p>
            </div>
            <div>
              <p className="text-sm text-green-600">Potential Offset</p>
              <p className="text-xl font-bold text-green-900">{renewableOffset.potential_offset_tco2?.toFixed(2)} tCO₂</p>
            </div>
            <div>
              <p className="text-sm text-green-600">Verified Offset</p>
              <p className="text-xl font-bold text-green-900">{renewableOffset.verified_offset_tco2?.toFixed(2)} tCO₂</p>
            </div>
          </div>
        </div>
      )}

      {/* Evidence List */}
      {evidence.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No evidence documents uploaded yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {evidence.map((e, i) => (
            <div key={i} className={`rounded-lg p-4 border ${e.verified ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}>
              <div className="flex items-start justify-between">
                <div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${e.evidence_type === 'renewable_certificate' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                    {e.evidence_type?.replace('_', ' ')}
                  </span>
                  <h4 className="font-semibold text-gray-900 mt-2">{e.title}</h4>
                  <p className="text-sm text-gray-600 mt-1">{e.description}</p>
                </div>
                {e.verified ? (
                  <span className="flex items-center gap-1 text-green-600 text-sm"><CheckCircle className="w-4 h-4" /> Verified</span>
                ) : (
                  <button onClick={() => verifyEvidence(e.id, true)} className="text-emerald-600 hover:text-emerald-800 text-sm font-medium">Verify</button>
                )}
              </div>
              {e.energy_kwh > 0 && (
                <p className="mt-2 text-sm text-gray-600">Energy: <strong>{e.energy_kwh?.toLocaleString()} kWh</strong></p>
              )}
              {e.certificate_id && (
                <p className="text-sm text-gray-500">Certificate: {e.certificate_id}</p>
              )}
              {e.document_url && (
                <a 
                  href={`${API}${e.document_url}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                >
                  <Download className="w-3 h-3" /> Download Document
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Evidence Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="bg-emerald-600 px-6 py-4 rounded-t-xl">
              <h3 className="text-lg font-bold text-white">Add Evidence Document</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Evidence Type</label>
                <select value={form.evidence_type} onChange={(e) => setForm({ ...form, evidence_type: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="renewable_certificate">Renewable Energy Certificate</option>
                  <option value="meter_reading">Meter Reading</option>
                  <option value="audit_report">Audit Report</option>
                  <option value="other">Other Document</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g., Solar REC Q1 2025" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload Document</label>
                <input 
                  type="file" 
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                />
                {selectedFile && (
                  <p className="text-sm text-gray-600 mt-1">Selected: {selectedFile.name}</p>
                )}
              </div>
              {form.evidence_type === 'renewable_certificate' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Energy (kWh)</label>
                      <input type="number" value={form.energy_kwh} onChange={(e) => setForm({ ...form, energy_kwh: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Certificate ID</label>
                      <input type="text" value={form.certificate_id} onChange={(e) => setForm({ ...form, certificate_id: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Issuer</label>
                    <input type="text" value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })} placeholder="e.g., IEX Green Markets" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>
                </>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <button onClick={() => { setShowForm(false); setSelectedFile(null); }} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button 
                  onClick={saveEvidence} 
                  disabled={uploading}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Save Evidence'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const WorkflowTab = ({ workflow, currencyInfo, studyId, onRefresh }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();

  const submitForValidation = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/cbam/${studyId}/workflow/submit-for-validation`, {}, { headers: { Authorization: `Bearer ${token}` } });
      onRefresh();
    } catch (error) {
      showError('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const validate = async (approved) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/cbam/${studyId}/workflow/validate?approved=${approved}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      onRefresh();
    } catch (error) {
      showError('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const approve = async (approved) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/cbam/${studyId}/workflow/approve?approved=${approved}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      onRefresh();
    } catch (error) {
      showError('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const exportCSV = () => {
    const token = localStorage.getItem('token');
    window.open(`${API}/api/cbam/${studyId}/export/csv?token=${token}`, '_blank');
  };

  const exportXML = () => {
    const token = localStorage.getItem('token');
    window.open(`${API}/api/cbam/${studyId}/export/xml?token=${token}`, '_blank');
  };

  const exportPDFReport = () => {
    const token = localStorage.getItem('token');
    window.open(`${API}/api/cbam/${studyId}/export/pdf-report?token=${token}`, '_blank');
  };

  const stages = workflow?.stages || {};
  const status = workflow?.status || 'draft';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Maker-Checker Approval Workflow</h3>
          <p className="text-sm text-gray-500">Energy Analyst → ESG Analyst → CFO</p>
        </div>
        {workflow?.can_generate_report && (
          <div className="flex gap-2">
            <button onClick={exportPDFReport} className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 flex items-center gap-2">
              <FileText className="w-4 h-4" /> CBAM Report (PDF)
            </button>
            <button onClick={exportCSV} className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 flex items-center gap-2">
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button onClick={exportXML} className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 flex items-center gap-2">
              <Download className="w-4 h-4" /> Export XML
            </button>
          </div>
        )}
      </div>

      {/* Currency Info */}
      {currencyInfo && !currencyInfo.is_eu_country && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
            <Globe className="w-4 h-4" /> Currency Conversion
          </h4>
          <p className="text-sm text-blue-800">
            Site location: <strong>{currencyInfo.country}</strong> (Non-EU)
          </p>
          <p className="text-sm text-blue-800">
            Local currency: <strong>{currencyInfo.local_currency}</strong> → EUR @ {currencyInfo.exchange_rate_to_eur}
          </p>
        </div>
      )}

      {/* Workflow Steps */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Step 1: Data Entry */}
        <div className={`rounded-xl p-6 border-2 ${stages.data_entry?.status === 'completed' ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${stages.data_entry?.status === 'completed' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
              {stages.data_entry?.status === 'completed' ? <Check className="w-5 h-5" /> : <span>1</span>}
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Data Entry</h4>
              <p className="text-xs text-gray-500">Energy Analyst</p>
            </div>
          </div>
          {stages.data_entry?.status === 'completed' ? (
            <div className="text-sm text-green-700">
              <p>Submitted by: {stages.data_entry.user}</p>
              <p className="text-xs">{stages.data_entry.timestamp}</p>
            </div>
          ) : (
            <button onClick={submitForValidation} className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700">
              Submit for Validation
            </button>
          )}
        </div>

        {/* Step 2: Validation */}
        <div className={`rounded-xl p-6 border-2 ${stages.validation?.status === 'approved' ? 'border-green-500 bg-green-50' : stages.validation?.status === 'rejected' ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white'}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${stages.validation?.status === 'approved' ? 'bg-green-500 text-white' : stages.validation?.status === 'rejected' ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
              {stages.validation?.status === 'approved' ? <Check className="w-5 h-5" /> : stages.validation?.status === 'rejected' ? <X className="w-5 h-5" /> : <span>2</span>}
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Validation</h4>
              <p className="text-xs text-gray-500">ESG Analyst</p>
            </div>
          </div>
          {stages.validation?.status === 'approved' || stages.validation?.status === 'rejected' ? (
            <div className={`text-sm ${stages.validation.status === 'approved' ? 'text-green-700' : 'text-red-700'}`}>
              <p>{stages.validation.status === 'approved' ? 'Approved' : 'Rejected'} by: {stages.validation.user}</p>
            </div>
          ) : status === 'pending_validation' ? (
            <div className="flex gap-2">
              <button onClick={() => validate(true)} className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">Approve</button>
              <button onClick={() => validate(false)} className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">Reject</button>
            </div>
          ) : (
            <p className="text-sm text-gray-400">Waiting for data entry</p>
          )}
        </div>

        {/* Step 3: Final Approval */}
        <div className={`rounded-xl p-6 border-2 ${stages.approval?.status === 'approved' ? 'border-green-500 bg-green-50' : stages.approval?.status === 'rejected' ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white'}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${stages.approval?.status === 'approved' ? 'bg-green-500 text-white' : stages.approval?.status === 'rejected' ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
              {stages.approval?.status === 'approved' ? <Check className="w-5 h-5" /> : stages.approval?.status === 'rejected' ? <X className="w-5 h-5" /> : <span>3</span>}
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Final Approval</h4>
              <p className="text-xs text-gray-500">CFO</p>
            </div>
          </div>
          {stages.approval?.status === 'approved' || stages.approval?.status === 'rejected' ? (
            <div className={`text-sm ${stages.approval.status === 'approved' ? 'text-green-700' : 'text-red-700'}`}>
              <p>{stages.approval.status === 'approved' ? 'Approved' : 'Rejected'} by: {stages.approval.user}</p>
            </div>
          ) : status === 'pending_approval' ? (
            <div className="flex gap-2">
              <button onClick={() => approve(true)} className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">Approve</button>
              <button onClick={() => approve(false)} className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">Reject</button>
            </div>
          ) : (
            <p className="text-sm text-gray-400">Waiting for validation</p>
          )}
        </div>
      </div>

      {/* Status Banner */}
      {workflow?.can_generate_report && (
        <div className="bg-green-100 border border-green-300 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div>
              <p className="font-semibold text-green-900">CBAM Report Approved</p>
              <p className="text-sm text-green-700">Ready for export and submission to EU authorities</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AuditLogTab = ({ logs, summary, filters, onFilterChange, onRefresh }) => {
  const [viewMode, setViewMode] = useState('logs'); // 'logs' or 'summary'

  const getEventColor = (event) => {
    if (event.includes('Added') || event.includes('Created')) return 'text-green-600 bg-green-50';
    if (event.includes('Updated') || event.includes('Modified')) return 'text-blue-600 bg-blue-50';
    if (event.includes('Deleted') || event.includes('Removed')) return 'text-red-600 bg-red-50';
    if (event.includes('Locked')) return 'text-purple-600 bg-purple-50';
    if (event.includes('Approved') || event.includes('Validated')) return 'text-emerald-600 bg-emerald-50';
    return 'text-gray-600 bg-gray-50';
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900">CBAM Audit Log</h3>
          <p className="text-sm text-gray-500">Track all changes for auditor verification</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('logs')}
            className={`px-4 py-2 rounded-lg font-medium ${viewMode === 'logs' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            <Database className="w-4 h-4 inline mr-2" />
            Logs
          </button>
          <button
            onClick={() => setViewMode('summary')}
            className={`px-4 py-2 rounded-lg font-medium ${viewMode === 'summary' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            <BarChart3 className="w-4 h-4 inline mr-2" />
            Summary
          </button>
          <button onClick={onRefresh} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Summary View */}
      {viewMode === 'summary' && summary && (
        <div className="space-y-6">
          {/* Overall Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-1">Total Events</h4>
              <p className="text-3xl font-bold text-blue-700">{summary.total_events || 0}</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-green-900 mb-1">Active Users</h4>
              <p className="text-3xl font-bold text-green-700">{summary.by_user?.length || 0}</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-purple-900 mb-1">Entity Types</h4>
              <p className="text-3xl font-bold text-purple-700">{summary.by_entity_type?.length || 0}</p>
            </div>
          </div>

          {/* By Entity Type */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Events by Entity Type</h4>
            <div className="space-y-3">
              {summary.by_entity_type?.map((item, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 capitalize">{item.entity_type?.replace('_', ' ')}</span>
                  <div className="flex items-center gap-3">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${(item.count / summary.total_events) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-bold text-gray-900 w-12 text-right">{item.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* By User */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Activity by User</h4>
            <div className="space-y-3">
              {summary.by_user?.slice(0, 10).map((item, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">{item.user}</span>
                  <div className="flex items-center gap-3">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-600 h-2 rounded-full"
                        style={{ width: `${(item.count / summary.total_events) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-bold text-gray-900 w-12 text-right">{item.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Recent Activity (Last 10)</h4>
            <div className="space-y-2">
              {summary.recent_activity?.map((log, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className={`px-2 py-1 rounded text-xs font-medium ${getEventColor(log.event)}`}>
                    {log.entity_type}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{log.event}</p>
                    <p className="text-xs text-gray-500">{log.user} • {formatTimestamp(log.timestamp)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Logs View */}
      {viewMode === 'logs' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Entity Type</label>
                <select
                  value={filters.entity_type}
                  onChange={(e) => onFilterChange({ ...filters, entity_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">All Types</option>
                  <option value="product">Product</option>
                  <option value="production">Production</option>
                  <option value="allocation">Allocation</option>
                  <option value="shipment">Shipment</option>
                  <option value="workflow">Workflow</option>
                  <option value="evidence">Evidence</option>
                  <option value="batch">Batch</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
                <input
                  type="text"
                  value={filters.user_email}
                  onChange={(e) => onFilterChange({ ...filters, user_email: e.target.value })}
                  placeholder="Filter by email..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          </div>

          {/* Logs List */}
          {logs && logs.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Database className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No audit logs found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {logs && logs.map((log, i) => (
                <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getEventColor(log.event)}`}>
                          {log.entity_type}
                        </span>
                        <h4 className="font-semibold text-gray-900">{log.event}</h4>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">User:</span>
                          <span className="ml-2 font-medium text-gray-900">{log.user}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Time:</span>
                          <span className="ml-2 font-medium text-gray-900">{formatTimestamp(log.timestamp)}</span>
                        </div>
                        {log.entity_id && (
                          <div className="col-span-2">
                            <span className="text-gray-500">Entity ID:</span>
                            <span className="ml-2 font-mono text-xs text-gray-700">{log.entity_id}</span>
                          </div>
                        )}
                      </div>
                      {log.changes && (
                        <div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200">
                          <p className="text-xs font-medium text-gray-700 mb-2">Changes:</p>
                          <pre className="text-xs text-gray-600 overflow-x-auto">{JSON.stringify(log.changes, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CBAMModule;
